import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch, Listen } from '@stencil/core';
import { PnLanguages, awaitTopbar, uuidv4, en, getTotalHeightOffset, reduceMotion } from '@/index';

import { translations } from './translations';

import type {
  DateType,
  GridDate,
  DateObject,
  DateObjectStrict,
  CalendarView,
  PnDatePickerSelectEvent,
  PnDatePickerFocusOptions,
} from '@/globals/date';
import {
  CALENDAR,
  MONTHS,
  YEARS,
  validateDate,
  isBefore,
  isAfter,
  selectedDate,
  getDateObject,
  getDiff,
  getToday,
  getDate,
  getReadableDate,
  getGrid,
  setYear,
  setMonth,
  navigateGrid,
} from '@/globals/date';
import { calendar, arrow_left, arrow_right, pn_return } from 'pn-design-assets/pn-assets/icons.js';

import type { Dayjs } from 'dayjs';

/**
 * The date picker allows a single or a range of dates to be selected.
 *
 * Based on the `format` prop, separators will automatically be added if you type the date.
 *
 * You can navigate the calendar grid with your keyboard.
 *
 * @nativeInput Use the `input` event to listen to content being modified by the user. It is emitted everytime a user writes or removes content in the input.
 * @nativeChange The `change` event is emitted when the input loses focus, the user clicks `Enter` or makes a selection (such as auto complete or suggestions).
 *
 * @slot chips - Introduce some quick date selectors underneath the calendar grid. Use the `pn-choice-chip` component.
 * @slot helpertext - You can use this slot instead of the prop `helpertext`. Recommended, only if you need to include additional HTML markup. Such as a `pn-text-link`. Use a `span` element to wrap the text and link.
 * @slot error - You can use this slot instead of the prop `error`. Recommended, only if you need to include additional HTML markup. Such as a `pn-text-link`. Use a `span` element to wrap the text and link.
 */
@Component({
  tag: 'pn-date-picker',
  styleUrl: 'pn-date-picker.scss',
})
export class PnDatePicker {
  id: string = `pn-date-picker-${uuidv4()}`;

  idFrom: string = `${this.id}-from`;
  idTo: string = `${this.id}-to`;
  idFromButton: string = `${this.id}-from-button`;
  idToButton: string = `${this.id}-to-button`;
  idHelper: string = `${this.id}-helper`;
  idError: string = `${this.id}-error`;
  idCalendar: string = `${this.id}-calendar`;

  calendarElement: HTMLDivElement;

  today: Dayjs = getToday();
  animation: Animation;

  animationDurationDefault: number = 400;
  animationDuration: number = this.animationDurationDefault;

  separators: { index: number; name: string }[] = [];
  separatorRegex: RegExp = /[^a-zA-Z\d\s:]/g;

  listMonths = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  listWeek = [1, 2, 3, 4, 5, 6, 0];

  @Element() hostElement: HTMLElement;

  @State() open: boolean = false;
  @State() openUp: boolean = false;

  @State() selectingTo: boolean = false;

  @State() grid: GridDate;

  @State() viewYearStart: number = null;

  @State() dateViewYear: number;
  @State() dateViewMonth: number;
  @State() dateViewDate: number;

  @State() showHelperSlot: boolean;
  @State() showErrorSlot: boolean;

  @State() isClosing = false;
  @State() isExpanding = false;

  /** Set a label for the from date. */
  @Prop() labelFrom!: string;
  /** Set a label for the to date. @see {@link range} */
  @Prop() labelTo?: string;
  /** Provide a helpertext for the date input. */
  @Prop() helpertext?: string;
  /** Set a predefined value for the from date. @see {@link format} */
  @Prop({ mutable: true }) start: string = '';
  /**
   * Set a predefined value for the from date.
   *
   * @see {@link range}
   * @see {@link format}
   */
  @Prop({ mutable: true }) end: string = '';
  /**
   * Set the date format of the value.
   *
   * While you can set any date value from the Dayjs documentation,
   * we strongly recommend you pick a simple format that you can also type manually.
   *
   * @see {@link https://day.js.org/docs/en/display/format Day.js format documentation.}
   */
  @Prop() format: string = 'YYYY-MM-DD';
  /** Manually set language; this will be inherited from the topbar. */
  @Prop() language?: PnLanguages = null;

  /** Set a custom ID for the calendar. @category HTML attributes */
  @Prop() dateId: string = this.id;
  /** HTML input name @category HTML attributes */
  @Prop() name?: string;
  /**
   * Placeholder for the input field (defaults to the format prop).
   * @see {@link format}
   * @category HTML attributes
   **/
  @Prop() placeholder?: string = this.format;
  /**
   * Placeholder for end date (defaults to the format prop).
   * @see {@link format}
   * @category HTML attributes
   **/
  @Prop() endPlaceholder?: string = this.format;
  /** Set the input `autocomplete` attribute. @category HTML attributes */
  @Prop() autocomplete?: string;
  /** Set the input `list` attribute for the first date input. @category HTML attributes */
  @Prop() list?: string;
  /** Set the input `list` attribute for the second date input. @category HTML attributes */
  @Prop() listEnd?: string;
  /** Set the HTML pattern prop on the input elements. Make sure it matches the format. @category HTML attributes */
  @Prop() pattern?: string;

  /** Allow the selection of a date range. @category Features */
  @Prop() range: boolean = false;
  /**
   * Set a limit on how many days one may select.
   * By default, you can select an unlimited range.
   *
   * @todo Create a range limit function.
   * @see {@link range}
   *
   * @category Features
   * @hide true
   **/
  @Prop() rangeLimit?: number;
  /** The calendar grid is shown as default. You can set either `months` or `years` as your first choice. @category Features */
  @Prop({ mutable: true }) view: CalendarView = CALENDAR;
  /** Make the calendar open upwards by default. Opens downwards if there is not enough space. @category Features */
  @Prop() calendarUp: boolean = false;
  /** Show weekend numbers to the left of the calendar grid. @category Features */
  @Prop() weekNumbers: boolean = false;
  /** Disable the automatic insertion of separators when typing in the input. @category Features */
  @Prop() disableTypeAhead: boolean = false;
  /** Remove the option to select dates on weekends. @category Features */
  @Prop() disableWeekends: boolean = false;
  /**
   * Individual dates you want to disable. Use a comma separated string.
   *
   * Remember to use the same format that you have in the `format` prop.
   * @see {@link format}
   * @example "YYYY-MM-DD,YYYY-MM-DD"
   * @category Features
   **/
  @Prop() disabledDates?: string;
  /** If you use a format with an unknown length, disable the max length. @category Features */
  @Prop() disableMaxLength?: boolean = false;

  /** Set the date picker as required. @category State */
  @Prop() required: boolean = false;
  /** Set the date picker as readonly. @category State */
  @Prop() readonly: boolean = false;
  /** Set the date picker as disabled. @category State */
  @Prop() disabled: boolean = false;

  /** Set an error message for the date picker. Overwrites the helpertext if used at the same time. @category Validation */
  @Prop() error?: string;
  /** Trigger the invalid state without an error message. @category Validation */
  @Prop() invalid: boolean = false;

  /**
   * Earliest date possible, this will determine how many years back the date picker will show.
   *
   * Remember to use the same format that you have in the `format` prop.
   * @see {@link format}
   * @example "2024-05-25"
   * @category Min/max date
   **/
  @Prop({ mutable: true }) minDate?: string = null;
  @Watch('minDate')
  watchMin() {
    if (this.minDate === null) return;
    if (!validateDate(this.minDate, this.format)) this.minDate = null;
  }

  /**
   * Latest date possible, this will determine how many years forward the date picker will show.
   *
   * Remember to use the same format that you have in the `format` prop.
   * @see {@link format}
   * @example "2024-06-25"
   * @category Min/max date
   **/
  @Prop({ mutable: true }) maxDate?: string = null;
  @Watch('maxDate')
  watchMax() {
    if (this.maxDate === null) return;
    if (!validateDate(this.maxDate, this.format)) this.maxDate = null;
  }

  @Watch('start')
  watchValue() {
    if (!validateDate(this.start, this.format)) return this.dateInvalid.emit({ start: this.start });

    const { year, month, date } = getDateObject(this.start, this.format);

    this.setViewYear({ year });
    this.setViewMonth({ month });
    this.setViewDate({ date });

    if (this.range && isAfter(this.start, this.end, this.format, 'date')) {
      this.end = '';
    }

    this.emitSelection();
  }

  @Watch('end')
  watchValueTo() {
    if (!this.range) return;
    if (!validateDate(this.end, this.format)) return this.dateInvalid.emit({ end: this.end });

    const date = getDate(this.end, this.format);

    this.setViewYear({ year: date.year() });
    this.setViewMonth({ month: date.month() });
    this.setViewDate({ date: date.date() });

    if (isAfter(this.start, this.end, this.format, 'date')) {
      const value = this.start;
      this.start = this.end;
      this.end = value;
    }

    this.emitSelection();
  }

  @Watch('format')
  handleFormat() {
    this.separators.length = 0;
    this.format
      .split('')
      .forEach((item, index) => this.separatorRegex.exec(item) && this.separators.push({ name: item, index }));
  }

  @Watch('id')
  watchId() {
    this.idFrom = `${this.dateId}-from`;
    this.idTo = `${this.dateId}-to`;
    this.idFromButton = `${this.id}-from-button`;
    this.idToButton = `${this.id}-to-button`;
    this.idHelper = `${this.dateId}-helper`;
    this.idError = `${this.dateId}-error`;
    this.idCalendar = `${this.dateId}-calendar`;
  }

  @Watch('dateViewYear')
  @Watch('dateViewMonth')
  @Watch('dateViewDate')
  watchView() {
    const data = this.getCurrentDateObject();
    if (validateDate(data, this.format)) this.updateGrid();
  }

  @Watch('open')
  watchOpen() {
    this.toggleCalendar.emit(this.open);

    this.gridHandler();

    if (this.open) this.addGlobalEventListeners();
    else return this.removeGlobalEventListeners();

    this.calendarElement.style.removeProperty('--pn-calendar-offset-left');

    this.openUp = this.calendarUp;

    requestAnimationFrame(() => {
      const rectHost = this.getRect(this.hostElement);

      const { scrollHeight } = this.calendarElement;
      const { innerHeight, innerWidth } = window;

      const offsetTop = getTotalHeightOffset();

      const spaceUpwards = rectHost.y - offsetTop;
      const spaceDownwards = innerHeight - rectHost.bottom;

      const fitUpwards = spaceUpwards > scrollHeight;
      const fitDownwards = spaceDownwards > scrollHeight;

      const openTop = (this.openUp && (fitUpwards || spaceUpwards > spaceDownwards)) || (!fitDownwards && fitUpwards);

      this.openUp = openTop;

      // Calc y-axis

      const rectCal = this.getRect(this.calendarElement);
      const widthMinusRight = innerWidth - rectCal.right;

      const offset = 0 > widthMinusRight ? widthMinusRight - 8 : 0;

      this.calendarElement.style.setProperty('--pn-calendar-offset-left', `${Math.floor(offset)}px`);
    });
  }

  @Watch('helpertext')
  @Watch('error')
  handleMessage() {
    this.checkSlottedHelper();
    this.checkSlottedError();
  }

  @Watch('view')
  handleView() {
    this.currentView.emit(this.view);
  }

  /**
   * Use the new `dateSelection`. Its here for compatibility. Will be removed in v8.
   * @deprecated Use the new `dateSelection`. Will be removed in v8.
   **/
  @Event() dateselection: EventEmitter<PnDatePickerSelectEvent>;

  /** Emits on valid date selection. Either if the user selects a date in the calendar or writes it manually. */
  @Event() dateSelection: EventEmitter<PnDatePickerSelectEvent>;
  emitSelection() {
    const data: PnDatePickerSelectEvent = {
      start: this.start,
    };

    if (this.range) {
      const days = getDiff(this.start, this.end, this.format);
      data.end = this.end;
      data.days = typeof days === 'number' ? days + 1 : null;
    }

    this.dateSelection.emit(data);
    this.dateselection.emit(data);
  }

  /** Emitted when an invalid value is set. This can only be done if the user writes in the input itself. */
  @Event() dateInvalid: EventEmitter<{ start?: string; end?: string }>;

  /** Emitted when the calendar is toggled. */
  @Event() toggleCalendar: EventEmitter<boolean>;
  /** Emmitted when you select a new view. */
  @Event() currentView: EventEmitter<CalendarView>;

  /**
   * If the select is open and you resize the window.
   * Remove all css props and disable the animations entierly.
   **/
  @Listen('resize', { target: 'window' })
  handleResize() {
    if (!this.open) return;
    this.toggleGrid(false);
  }

  async componentWillLoad() {
    this.watchMin();
    this.watchMax();
    this.watchId();

    this.handleFormat();
    this.handleMessage();

    const valid = validateDate(this.start || this.end, this.format);
    const data = valid && getDateObject(this.start || this.end, this.format);

    const { year, month, date } = getDateObject(this.today);

    this.setViewDate({ date: data.date || date });
    this.setViewMonth({ month: data.month || month });
    this.setViewYear({ year: data.year || year });

    if (this.language === null) await awaitTopbar(this.hostElement);
  }

  // Animation

  private gridHandler() {
    if (this.open) this.openGrid();
    else this.closeGrid();
  }

  private openGrid() {
    requestAnimationFrame(() => {
      const { clientHeight, scrollHeight } = this.calendarElement;
      const height = this.isClosing ? clientHeight : 0;
      this.calendarElement.style.height = `${scrollHeight}px`;

      this.isExpanding = true;
      this.animateGrid(true, `${height}px`, `${this.calendarElement.scrollHeight}px`);
    });
  }

  private closeGrid() {
    const { scrollHeight, clientHeight } = this.calendarElement;
    const height = this.isExpanding ? clientHeight : scrollHeight;
    this.calendarElement.style.height = `0px`;

    this.isClosing = true;
    this.animateGrid(false, `${height}px`, `0px`);
  }

  private animateGrid(open: boolean, startHeight: string, endHeight: string) {
    this.cancelAnimations();

    if (reduceMotion()) this.animationDuration = 0;
    else this.animationDuration = this.animationDurationDefault;

    this.animation = this.calendarElement.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: this.animationDuration,
        easing: 'cubic-bezier(0.6, 0, 0.2, 1)',
      },
    );
    this.animation.onfinish = () => this.animationFinish();
    this.animation.oncancel = () => (open ? (this.isExpanding = false) : (this.isClosing = false));
  }

  private animationFinish() {
    this.cancelAnimations();
    this.calendarElement.style.height = this.isClosing ? '0px' : '';
    this.isClosing = false;
    this.isExpanding = false;
  }

  private cancelAnimations() {
    if (this.animation) this.animation.cancel();
  }
  // End of Animation

  private globalEvents = (event: PointerEvent | KeyboardEvent) => {
    const target = event.target as HTMLElement;
    const isWithinCalendar = target?.closest(this.hostElement.localName);
    if (!isWithinCalendar) this.toggleGrid(false);
  };

  private addGlobalEventListeners() {
    const root: Document = this.hostElement.getRootNode() as Document;

    root.addEventListener('click', this.globalEvents);
  }

  private removeGlobalEventListeners() {
    const root: Document = this.hostElement.getRootNode() as Document;

    root.removeEventListener('click', this.globalEvents);
  }

  private translate(prop: string): string {
    return translations?.[prop?.toUpperCase()]?.[this.language || en] || prop?.toUpperCase();
  }

  private translateDateText(customDate: DateObject, format: string): string {
    return getReadableDate({ ...this.getCurrentDateObject(), ...customDate }, this.language, format);
  }

  private getRect(element: HTMLElement): DOMRect {
    return element.getBoundingClientRect();
  }

  private toggleGrid(state?: boolean, selecting?: boolean) {
    this.open = state ?? !this.open;
    this.selectingTo = selecting;
  }

  private hasHelperText(): boolean {
    return this.helpertext?.length > 0 || this.showHelperSlot;
  }

  /** If any `error` text is present, either via prop/slot. */
  private hasErrorMessage(): boolean {
    return this.error?.length > 0 || this.showErrorSlot;
  }

  /** If any `error` is active, either via the prop `invalid` or `error` prop/slot. */
  private hasError(): boolean {
    return this.hasErrorMessage() || this.invalid || this.showErrorSlot;
  }

  private checkSlottedHelper() {
    const slottedHelper = this.hostElement.querySelector<HTMLElement>('[slot=helpertext]')?.textContent;
    this.showHelperSlot = !!slottedHelper?.length;
  }

  private checkSlottedError() {
    const slottedError = this.hostElement.querySelector<HTMLElement>('[slot=error]')?.textContent;
    this.showErrorSlot = !!slottedError?.length;
  }

  private viewingCalendar(): boolean {
    return this.view === CALENDAR;
  }

  private viewingMonth(): boolean {
    return this.view === MONTHS;
  }

  private viewingYears(): boolean {
    return this.view === YEARS;
  }

  private viewType(): DateType {
    return this.viewingCalendar() ? 'date' : this.viewingMonth() ? 'month' : 'year';
  }

  private isBeforeMax(data: DateObjectStrict): boolean {
    if (this.maxDate) return isBefore(data, this.maxDate, this.format, this.viewType());
    return true;
  }

  private isAfterMin(data: DateObjectStrict): boolean {
    if (this.minDate) return isAfter(data, this.minDate, this.format, this.viewType());
    return true;
  }

  private isDisabledWeekend(day: number): boolean {
    if (!this.viewingCalendar()) return false;
    return this.disableWeekends ? day >= 6 : false;
  }

  private isDisabledDate(data: DateObjectStrict): boolean {
    if (!this.disabledDates?.length || !this.viewingCalendar()) return false;
    const list = this.disabledDates.split(',');
    return !!list.find(disabledDate => selectedDate(disabledDate, data, this.format, this.viewType()));
  }

  private isDisabled(data: DateObjectStrict): {
    disabled: boolean;
    manualDisable: boolean;
    weekendDisable: boolean;
    minMaxDisable: boolean;
  } {
    const isAfterMaxDate = !this.isBeforeMax(data);
    const isBeforeMinDate = !this.isAfterMin(data);

    const weekendDisable = this.viewingCalendar() && this.isDisabledWeekend(data.day);

    const manualDisable = this.isDisabledDate(data);

    const disabled = isAfterMaxDate || isBeforeMinDate || weekendDisable || manualDisable;

    return {
      disabled,
      manualDisable,
      weekendDisable,
      minMaxDisable: isAfterMaxDate || isBeforeMinDate,
    };
  }

  private updateGrid() {
    this.grid = getGrid(this.dateViewYear, this.dateViewMonth);
  }

  private getCurrentViewDate(data: DateObjectStrict = this.getCurrentDateObject()): string {
    return getReadableDate(data, this.language, 'MMMM YYYY');
  }

  private isSelected(data: DateObject, to: boolean = false): boolean {
    const value = to ? this.end : this.start;
    return selectedDate(value, this.getCurrentDateObject(data), this.format, this.viewType());
  }

  private isToday(data: DateObject): boolean {
    return selectedDate(this.today.format(this.format), this.getCurrentDateObject(data), this.format, this.viewType());
  }

  private getCurrentDateObject({
    year = this.dateViewYear,
    month = this.dateViewMonth,
    date = this.dateViewDate,
    day,
  }: DateObject = {}) {
    return {
      year,
      month,
      date,
      day,
    };
  }

  /** Handle keyboard navigation in the calendar grid. */
  private calendarKeyboardNavigation(event: KeyboardEvent, data: DateObjectStrict, disabled: boolean = false) {
    const validCodes = [
      'Enter',
      'Space',
      'ArrowRight',
      'ArrowLeft',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'PageDown',
      'PageUp',
      'Escape',
    ];

    if (!validCodes.includes(event.code)) return;

    event.preventDefault();
    if (event.code === 'Escape') return this.toggleGrid(false);

    const select = !disabled && event.code.match(/^(Enter|Space)$/);

    if (select && this.viewingYears()) return this.setViewYear({ year: data.year, reset: true });
    if (select && this.viewingMonth()) return this.setViewMonth({ month: data.month, grid: true, reset: true });
    if (select && this.viewingCalendar()) return this.setValue(data.date);

    const goToDate = this.navDirection(event, data);

    if (!goToDate) return;

    const { year, month, date } = goToDate;

    this.setViewYear({ year });
    this.setViewMonth({ month, grid: true });
    this.setViewDate({ date });

    this.resetFocus();
  }

  private navDirection(event: KeyboardEvent, data: DateObjectStrict): DateObjectStrict {
    const { code } = event;

    const nextDate = navigateGrid(code, data, this.disableWeekends, this.viewType());
    const date = getDateObject(nextDate);

    return date;
  }

  private getYearGrid(): number[] {
    const list = [];

    let oldestInList = this.viewYearStart - 7;

    for (let i = 0; 15 > i; i++) {
      list.push(oldestInList++);
    }

    return list;
  }

  /** Defaults to the calendar view. */
  private setView(view: CalendarView) {
    this.view = view;
    requestAnimationFrame(() => this.focusCalendar());
  }

  private setNavView(data: { minus?: boolean; plus?: boolean; grid?: boolean }) {
    if (this.viewingYears()) return this.setViewYear({ ...data, grid: true });
    return this.setViewMonth(data);
  }

  private setViewYear({
    year = this.dateViewYear,
    minus = false,
    plus = false,
    reset = false,
    grid = false,
  }: {
    year?: number;
    minus?: boolean;
    plus?: boolean;
    reset?: boolean;
    grid?: boolean;
  }) {
    const nextYear = setYear({ year, minus, plus });

    const start = this.viewYearStart;
    const max = start + 7;
    const min = start - 7;

    if (grid) {
      const minusVal = this.viewYearStart - 15;
      const plusVal = this.viewYearStart + 15;
      this.viewYearStart = minus ? minusVal : plusVal;
      this.dateViewYear = this.viewYearStart;
    } else this.dateViewYear = nextYear;

    if (start === null || nextYear > max || min > nextYear) this.viewYearStart = nextYear;

    if (reset) this.setView(CALENDAR);
  }

  private setViewMonth({
    month = this.dateViewMonth,
    minus = false,
    plus = false,
    reset = false,
    grid = false,
  }: {
    month?: number;
    minus?: boolean;
    plus?: boolean;
    reset?: boolean;
    grid?: boolean;
  }) {
    const nextMonth = setMonth({ month, minus, plus });

    this.dateViewMonth = nextMonth;

    if (!grid && month === 0 && minus) this.setViewYear({ minus: true });
    if (!grid && month === 11 && plus) this.setViewYear({ plus: true });

    if (reset) this.setView(CALENDAR);
  }

  private setViewDate({ date }: { date: number }) {
    this.dateViewDate = date;
  }

  private handleSeparator(event: InputEvent) {
    let value = (event.target as HTMLInputElement).value;

    const foundSeparator = this.separators.find(({ index }) => index === value.length);
    if (foundSeparator?.name) {
      value += foundSeparator.name;
    }

    return value;
  }

  private inputHandler(event: InputEvent, to: boolean = false) {
    const propName = to ? 'end' : 'start';
    const value = this.disableTypeAhead ? (event.target as HTMLInputElement).value : this.handleSeparator(event);

    this[propName] = value;
  }

  private setValue(date: number) {
    const value = getReadableDate(this.getCurrentDateObject({ date }), this.language, this.format);

    if (this.selectingTo) this.end = value;
    else this.start = value;

    this.setViewDate({ date });

    if (!this.range || this.selectingTo) {
      this.toggleGrid(false);
      this.focusToggleCalendarButton();
    }

    if (this.range && !this.selectingTo) this.selectingTo = true;
  }

  private getDayAttributes(dateObject: DateObject, blank: boolean): { [a: string]: any } {
    const data = this.getCurrentDateObject(dateObject);
    const { disabled } = this.isDisabled(data);

    if (blank) return { 'data-blank': true };

    function capitalize(text: string) {
      return text.charAt(0).toUpperCase() + text.slice(1);
    }

    const type = this.viewType();
    const value = data[type];

    const disabledProp = type !== 'date' ? 'disabled' : 'aria-disabled';
    const selectedProp = type !== 'date' ? 'aria-pressed' : 'aria-selected';

    const selectedFrom = this.isSelected(data);
    const selectedTo = this.isSelected(data, true);

    const selected = selectedFrom || selectedTo;

    const after = this.range && isAfter(data, this.start, this.format, this.viewType());
    const before = this.range && isBefore(data, this.end, this.format, this.viewType());
    const isBetween = after && before;

    const isDisabled = disabled && (type === 'date' || !selected);

    const tabbable = this[`dateView${capitalize(type)}`] === value;

    const props: { [a: string]: any } = {
      'onKeyDown': (e: KeyboardEvent) => this.calendarKeyboardNavigation(e, data, isDisabled),
      'tabindex': tabbable ? '0' : '-1',
      [selectedProp]: (selected || isBetween)?.toString(),
      'aria-current': this.isToday(data) ? 'date' : null,
      'data-active': isDisabled ? null : selected,
      'data-today': this.isToday(data),
      'data-option': 'true',
      [`data-${type}`]: value,
      [disabledProp]: isDisabled ? 'true' : null,
    };

    const singleDate = selectedDate(this.start, this.end, this.format, 'date');

    if (this.range && !singleDate) {
      props['data-range'] = isBetween;
      props['data-range-from'] = selectedFrom && this.end !== '';
      props['data-range-to'] = selectedTo && this.start !== '';
    } else {
      props['data-single'] = true;
    }

    if (this.viewingCalendar()) props.onClick = () => this.setValue(data.date);
    if (this.viewingMonth()) props.onClick = () => this.setViewMonth({ month: data.month, reset: true });
    if (this.viewingYears()) props.onClick = () => this.setViewYear({ year: data.year, reset: true });

    if (isDisabled) delete props.onClick;

    return props;
  }

  /** Focus the button toggling the calendar. Handles the start/from date on its own. */
  private focusToggleCalendarButton() {
    requestAnimationFrame(() => {
      const id = this.range ? this.idToButton : this.idFromButton;
      this.hostElement.querySelector<HTMLButtonElement>(`#${id}`).focus({ preventScroll: true });
    });
  }

  private focusCalendar() {
    requestAnimationFrame(() => {
      const element = this.calendarTabElement({ first: true, grid: true });

      element?.focus({ preventScroll: true });
    });
  }

  private resetFocus() {
    if (this.open) return this.focusCalendar();
    else this.focusToggleCalendarButton();
  }

  private handleButtonBlur(event: KeyboardEvent) {
    if (event.code === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    if (this.open && event.key === 'Tab') return this.focusCalendar();
  }

  /**
   * This function queries all tabbable elements inside the calendar popup.
   * Since we allow slotted content it important that we have a function that takes all elements into account.
   * With the `first` and `grid` argument, you can decide which one you want to get.
   * There are fallbacks so you should never get an empty list of elements.
   */
  private calendarTabElement({ first, grid }: PnDatePickerFocusOptions): HTMLElement {
    const focusableElements = ':is(input, select, button:not([tabindex="-1"]), td[tabindex="0"])';
    const elements = this.calendarElement.querySelectorAll<HTMLElement>(focusableElements);
    const list = Array.from(elements).filter(
      ({ localName, offsetParent }) => focusableElements.includes(localName) && offsetParent !== null,
    );

    const gridEl = list.find(({ dataset, tabIndex }) => dataset.option && tabIndex === 0);
    if (grid && gridEl) return gridEl;
    if (first) return list.shift();
    else return list.pop();
  }

  /**
   * We need to listen to the `Esc` and `Tab` key for the entire calendar popup.
   * Regardless if you focus the grid, the nav buttons or slotted content,
   * the popup will close if you press `Esc`. We also need to reset the focus when the user tabs.
   */
  private handleCalendarTabEsc(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    const tabElement = this.calendarTabElement({ first: event.shiftKey });

    event.stopImmediatePropagation();

    if (event.code === 'Escape') {
      event.preventDefault();

      this.toggleGrid(false);
      this.focusToggleCalendarButton();
      return;
    }

    if (event.code === 'Tab' && target.isEqualNode(tabElement)) {
      event.preventDefault();
      this.calendarTabElement({ first: !event.shiftKey }).focus({ preventScroll: true });
    }
  }

  private ariaDescribedby(): string {
    const list = [];
    if (this.hasErrorMessage()) list.push(this.idError);
    else if (this.hasHelperText()) list.push(this.idHelper);
    return list.length ? list.join(' ') : null;
  }

  /** Renders the date calendar grid.  */
  private renderDateGrid(): HTMLTableElement {
    return (
      <table role="grid" class="pn-date-picker-table" aria-multiselectable={this.range ? 'true' : null}>
        <caption class="pn-date-picker-sr-only" key={this.getCurrentViewDate()}>
          {this.getCurrentViewDate()}
        </caption>
        <thead class="pn-date-picker-thead">
          <tr class="pn-date-picker-tr">
            {this.weekNumbers && <th class="pn-date-picker-th" scope="col" aria-hidden="true"></th>}
            {this.listWeek.map(index => (
              <th class="pn-date-picker-th" scope="col" abbr={this.translateDateText({ day: index }, 'dddd')}>
                {this.translateDateText({ day: index }, 'ddd')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody class="pn-date-picker-tbody">
          {this.grid?.map(({ week, list }) => (
            <tr key={`${this.dateViewYear}-${week}`} class="pn-date-picker-tr">
              {this.weekNumbers && (
                <td
                  class="pn-date-picker-td"
                  data-blank
                  data-week
                  title={`${this.translate('WEEK_NAME')} ${week}`}
                  aria-hidden="true"
                >
                  <span class="pn-date-picker-td-week">{week}</span>
                </td>
              )}
              {list.map(({ day, date, blank }) => (
                <td
                  key={`${this.dateViewYear}-${this.dateViewMonth}-${date}`}
                  class="pn-date-picker-td"
                  {...this.getDayAttributes({ date, day }, blank)}
                >
                  <span class="pn-date-picker-td-text">{date}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  /** Renders the month calendar grid.  */
  private renderMonthGrid(): HTMLTableElement {
    return (
      <ul class="pn-date-picker-list">
        {this.listMonths.map(month => (
          <li key={month} class="pn-date-picker-item" data-item="month">
            <button type="button" class="pn-date-picker-button" {...this.getDayAttributes({ month }, false)}>
              <span class="pn-date-picker-month" data-full>
                {this.translateDateText({ month }, 'MMMM')}
              </span>
              <span class="pn-date-picker-month" data-abbr>
                {this.translateDateText({ month }, 'MMM')}
              </span>
            </button>
          </li>
        ))}
      </ul>
    );
  }

  /** Renders the year calendar grid.  */
  private renderYearGrid(): HTMLTableElement {
    return (
      <ul class="pn-date-picker-list">
        {this.getYearGrid()?.map(year => (
          <li key={year} class="pn-date-picker-item" data-item="year">
            <button type="button" class="pn-date-picker-button" {...this.getDayAttributes({ year }, false)}>
              <span>{year}</span>
            </button>
          </li>
        ))}
      </ul>
    );
  }

  private renderInput({ to = false }: { to?: boolean } = {}) {
    const id = to ? this.idTo : this.idFrom;
    const idButton = to ? this.idToButton : this.idFromButton;
    const label = to ? this.labelTo : this.labelFrom;
    const value = to ? this.end : this.start;
    const placeholder = to ? this.endPlaceholder : this.placeholder;
    const list = to ? this.listEnd : this.list;

    const editing = this.open ? (this.selectingTo ? to : !to) : false;

    const defaultText = this.translate('SELECT_DATE') as string;

    const textProp = this.range ? (to ? 'END_' : 'START_') : '';
    const dateText = this.translate(`SELECTED_${textProp}DATE`) as string;

    let textButton = defaultText;
    if (value) {
      textButton += `, ${dateText.replace('{date}', value)}`;
    }

    const showButton = !(this.disabled || this.readonly);

    return (
      <div class="pn-date-picker-container" data-error={this.hasError()}>
        <label class="pn-date-picker-label" htmlFor={id}>
          <span>{label}</span>
        </label>
        <div class="pn-date-picker-field">
          <input
            type="text"
            id={id}
            class="pn-date-picker-input"
            name={this.name}
            placeholder={placeholder}
            autocomplete={this.autocomplete}
            maxlength={this.disableMaxLength ? null : this.format.length}
            list={list}
            pattern={this.pattern}
            value={value}
            disabled={this.disabled}
            required={this.required}
            readonly={this.readonly}
            aria-describedby={this.ariaDescribedby()}
            aria-invalid={this.hasError()?.toString()}
            onInput={e => this.inputHandler(e, to)}
            data-active={editing}
          />
          {showButton && (
            <pn-button
              class="pn-date-picker-toggle"
              buttonId={idButton}
              icon={calendar}
              iconOnly={true}
              appearance="light"
              arialabel={textButton}
              ariaexpanded={this.open.toString()}
              ariacontrols={this.idCalendar}
              data-active={this.open}
              data-input
              small
              onPnClick={() => this.toggleGrid(null, to)}
              onKeyDown={e => this.handleButtonBlur(e)}
            />
          )}
        </div>
      </div>
    );
  }

  render() {
    return (
      <Host>
        <div class="pn-date-picker">
          {this.renderInput()}

          {this.range && (
            <div class="pn-date-picker-range-icon test">
              <pn-icon icon={arrow_right} />
            </div>
          )}

          {this.range && this.renderInput({ to: this.range })}
        </div>

        <div
          id={this.idCalendar}
          class="pn-date-picker-calendar"
          role="dialog"
          aria-label={this.translate('CALENDAR_NAVIGATION')}
          data-open={this.open}
          data-moving={this.isClosing || this.isExpanding}
          data-direction={this.openUp ? 'top' : 'bottom'}
          data-range={this.range}
          style={{ height: '0px' }}
          ref={el => (this.calendarElement = el)}
          onKeyDown={e => this.handleCalendarTabEsc(e)}
        >
          <div class="pn-date-picker-wrapper">
            <nav class="pn-date-picker-nav" aria-labelledby={this.idCalendar}>
              <pn-button
                hidden={this.viewingMonth()}
                small
                appearance="light"
                arialabel={this.translate(`PREVIOUS_${this.viewType().toUpperCase()}`) as string}
                icon={arrow_left}
                iconOnly={true}
                onPnClick={() => this.setNavView({ minus: true })}
              />

              <pn-button
                hidden={!this.viewingCalendar()}
                small
                appearance="light"
                onPnClick={() => this.setView(MONTHS)}
              >
                <span class="pn-date-picker-month" data-full>
                  {this.translateDateText({ date: 1 }, 'MMMM')}
                </span>
                <span class="pn-date-picker-month" data-abbr>
                  {this.translateDateText({ date: 1 }, 'MMM')}
                </span>
              </pn-button>

              <h2 hidden={this.viewingCalendar()} class="pn-date-picker-title">
                {this.translate(`SELECT_${this.viewType().toUpperCase()}`)}
              </h2>

              <pn-button
                hidden={!this.viewingCalendar()}
                small
                appearance="light"
                onPnClick={() => this.setView(YEARS)}
              >
                <span>{this.dateViewYear}</span>
              </pn-button>

              <pn-button
                hidden={this.viewingMonth()}
                small
                appearance="light"
                arialabel={this.translate(`NEXT_${this.viewType().toUpperCase()}`) as string}
                icon={arrow_right}
                iconOnly={true}
                onPnClick={() => this.setNavView({ plus: true })}
              />
            </nav>

            {this.viewingYears() && this.renderYearGrid()}
            {this.viewingMonth() && this.renderMonthGrid()}
            {this.viewingCalendar() && this.renderDateGrid()}

            <aside class="pn-date-picker-chips">
              <slot name="chips"></slot>
            </aside>

            <nav class="pn-date-picker-bottom" hidden={this.viewingCalendar()}>
              <pn-button
                appearance="light"
                variant="outlined"
                small
                icon={pn_return}
                onPnClick={() => this.setView(CALENDAR)}
              >
                <span>{this.translate('GO_CALENDAR')}</span>
              </pn-button>
            </nav>
          </div>
        </div>

        <p id={this.idHelper} class="pn-date-picker-helpertext" hidden={!this.hasHelperText() || this.hasError()}>
          <span>{this.helpertext}</span>
          <slot name="helpertext" />
        </p>

        <p id={this.idError} class="pn-date-picker-error" role="alert" hidden={!this.hasErrorMessage()}>
          <span>{this.error}</span>
          <slot name="error" />
        </p>
      </Host>
    );
  }
}
